'use client';

import { useEffect, useRef, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { initSocket, disconnectSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/authStore';
import { useChatStore, GenderType } from '@/store/chatStore';
import { playNotificationSound, getOrCreateSessionToken } from '@/lib/utils';
import { api } from '@/lib/api';
import { useUIStore } from '@/store/uiStore';

export function useSocket() {
  const socketRef = useRef<Socket | null>(null);
  const { token, sessionToken, setSessionToken, user, isAuthenticated } = useAuthStore();
  const {
    setStatus,
    addMessage,        // used by message:receive handler
    addSystemMessage,
    setRoomId,
    setStrangerTyping,
    clearChat,
    setCurrentStrangerId,
    soundEnabled,
    guestDisplayName,
    myGender,
    genderFilter,
    setStrangerGender,
    setStrangerDisplayName,
    setStrangerIsMember,
    setStrangerId,
    setFriendRequestStatus,
    setFriendRequestFrom,
    incrementLifetimeConnections,
  } = useChatStore();
  const { addToast } = useUIStore();

  const initializeSocket = useCallback(async () => {
    let sToken = sessionToken;
    if (!token && !isAuthenticated && !sToken) {
      // Only create an anonymous session token when the user is genuinely not
      // logged in. If isAuthenticated is true, the httpOnly cookie carries the
      // JWT and socket/index.js will read it from the handshake cookie header.
      sToken = getOrCreateSessionToken();
      setSessionToken(sToken);
    }

    // Pass token if in memory; pass sessionToken only for anonymous users.
    // When isAuthenticated but token is null (page reload), pass neither —
    // the browser sends the httpOnly cookie automatically via withCredentials.
    const sock = initSocket(
      token || undefined,
      isAuthenticated ? undefined : (sToken || undefined)
    );
    socketRef.current = sock;

    // ── Matching events ─────────────────────────────────────────────────────
    sock.on('status:searching', () => {
      setStatus('searching');
      clearChat();
      addSystemMessage('Looking for a stranger...');
    });

    sock.on(
      'stranger:matched',
      ({
        roomId,
        strangerGender,
        strangerDisplayName,
        strangerIsMember,
        strangerId,
      }: {
        roomId: string;
        strangerGender?: GenderType;
        strangerDisplayName?: string;
        strangerIsMember?: boolean;
        strangerId?: string | null;
      }) => {
        setStatus('connected');
        if (isAuthenticated && token) {
          api.auth.incrementConnections(token)
            .then((data) => {
              if (data && typeof data.lifetimeConnections === 'number') {
                useChatStore.getState().setLifetimeConnections(data.lifetimeConnections);
              }
            })
            .catch(() => incrementLifetimeConnections());
        } else {
          incrementLifetimeConnections();
        }
        setRoomId(roomId);
        clearChat();

        // Update stranger info
        setStrangerGender(strangerGender || null);
        setStrangerDisplayName(strangerDisplayName || null);
        setStrangerIsMember(!!strangerIsMember);
        setStrangerId(strangerId || null);

        const genderLabel = strangerGender ? ` (${strangerGender === 'MALE' ? 'M' : strangerGender === 'FEMALE' ? 'F' : '?'})` : '';
        const nameLabel = strangerDisplayName ? ` · ${strangerDisplayName}` : '';
        addSystemMessage(`You are now connected to a stranger${genderLabel}${nameLabel}. Say hi!`);
        if (soundEnabled) playNotificationSound();
      }
    );

    // ── Friend request events ────────────────────────────────────────────────
    sock.on('friend:request_sent', () => {
      setFriendRequestStatus('sent');
    });

    sock.on('friend:request_received', ({ fromId, fromName }: { fromId: string; fromName: string }) => {
      setFriendRequestStatus('received');
      setFriendRequestFrom({ id: fromId, name: fromName });
    });

    sock.on('friend:accepted', ({ name }: { userId: string; name: string }) => {
      setFriendRequestStatus('accepted');
      addSystemMessage(`You and ${name} are now friends! You can message each other privately.`);
    });

    sock.on('friend:rejected', () => {
      setFriendRequestStatus('rejected');
      addSystemMessage('Friend request was declined.');
    });

    sock.on('friend:error', ({ message }: { message: string }) => {
      addToast({ message, type: 'error' });
    });

    sock.on('stranger:disconnected', ({ message }: { message: string }) => {
      clearChat();
      setStatus('disconnected');
      setCurrentStrangerId(null);
      setStrangerGender(null);
      setStrangerDisplayName(null);
      addSystemMessage(message || 'Stranger has disconnected.');
    });

    sock.on('status:disconnected', () => {
      setStatus('disconnected');
      clearChat();
    });

    // ── Chat events ──────────────────────────────────────────────────────────
    sock.on(
      'message:receive',
      (msg: {
        id: string;
        content: string;
        senderId: string;
        timestamp: number;
        type: 'TEXT' | 'SYSTEM';
        flagged?: boolean;
      }) => {
        const isMe = msg.senderId === sock.id;
        addMessage({ ...msg, isMe });
        if (!isMe && soundEnabled) playNotificationSound();
      }
    );

    sock.on('message:blocked', ({ reason }: { reason: string }) => {
      addToast({ message: `Message blocked: ${reason}`, type: 'error' });
    });

    // ── File Attachments ─────────────────────────────────────────────────────
    sock.on('attachment:data', ({ data, mimeType, filename }: { data: string; mimeType: string; filename: string }) => {
      addMessage({
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        content: filename,
        senderId: sock.id || 'stranger',
        isMe: false,
        timestamp: Date.now(),
        type: 'ATTACHMENT',
        attachment: { dataUrl: data, mimeType, filename },
      });
      if (soundEnabled) playNotificationSound();
    });

    // ── Typing ───────────────────────────────────────────────────────────────
    sock.on('typing:start', () => setStrangerTyping(true));
    sock.on('typing:stop', () => setStrangerTyping(false));

    // ── Errors ───────────────────────────────────────────────────────────────
    sock.on('error:match', ({ message }: { message: string }) => {
      addToast({ message, type: 'error' });
      setStatus('idle');
    });

    sock.on('error:chat', ({ message }: { message: string }) => {
      addToast({ message, type: 'error' });
    });

    return sock;
  }, [token, sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initializeSocket();

    return () => {
      socketRef.current?.removeAllListeners();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When Zustand hydrates and token becomes available after socket already
  // connected without auth (page-reload race), reconnect with the real token.
  useEffect(() => {
    const sock = socketRef.current;
    if (!sock || !token) return;
    const hasAuth = !!(sock as Socket & { auth?: { token?: string } }).auth?.token;
    if (sock.connected && !hasAuth) {
      (sock as Socket & { auth: { token: string } }).auth = { token };
      sock.disconnect().connect();
    }
  }, [token]);

  const findStranger = useCallback(
    (preferGender?: 'FEMALE', turnstileToken?: string | null) => {
      const state = useChatStore.getState();
      // Include gender + displayName for anonymous users
      const myGenderVal = state.myGender || (user?.gender as GenderType | null) || null;
      const displayName = state.guestDisplayName || user?.username || null;

      socketRef.current?.emit('find:stranger', {
        preferGender: preferGender || null,
        gender: myGenderVal,
        displayName,
        turnstileToken: turnstileToken || undefined,
      });
    },
    [user]
  );

  const skipStranger = useCallback(() => {
    socketRef.current?.emit('skip:stranger');
  }, []);

  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current) return;
    socketRef.current.emit('message:send', { content });
    // Don't add locally — server echoes back via message:receive, preventing duplicates
  }, []);

  const sendTypingStart = useCallback(() => {
    socketRef.current?.emit('typing:start');
  }, []);

  const sendTypingStop = useCallback(() => {
    socketRef.current?.emit('typing:stop');
  }, []);

  const leaveChat = useCallback(() => {
    socketRef.current?.emit('chat:leave');
    setStatus('idle');
    clearChat();
    setRoomId(null);
  }, [setStatus, clearChat, setRoomId]);

  const sendFriendRequest = useCallback(() => {
    socketRef.current?.emit('friend:request');
  }, []);

  const acceptFriendRequest = useCallback(() => {
    socketRef.current?.emit('friend:accept');
  }, []);

  const rejectFriendRequest = useCallback(() => {
    socketRef.current?.emit('friend:reject');
  }, []);

  return {
    socket: socketRef.current,
    socketRef,
    findStranger,
    skipStranger,
    sendMessage,
    sendTypingStart,
    sendTypingStop,
    leaveChat,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
  };
}
